import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSpoolNcrList } from '../../../../../../../redux/Spoolsbase/actions';
import { filterNcrRowsByWeldId, type NcrRow } from '../../spoolNcrApi';
import { WorkflowNcrSummaryTable } from '../weld/WorkflowNcrSummaryTable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Button } from '../../../../../../../components/ui/button';
import { Input } from '../../../../../../../components/ui/input';
import { AntdDate } from '../../../../../../../components/ui/antd-date';
import { Label } from '../../../../../../../components/ui/label';
import AddNewItemDialog from '../../../../../../../components/ui/AddNewItemDialog';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../../../redux/Project/materialSaga';
import { SearchableSelect } from '../../../../../../../components/ui/SearchableSelect';
import { WELD_FITUP_BEVEL_METHOD_TAGS, type DocumentTag, type SpecificationDocument } from '../../../../../types';
import { convertFitmentReadingsObject, getUnitFromSettings, getUnitLabel } from '../../../../../../../utils/unitUtils';
import type { WeldJoint } from '../../SpoolsProduction';
import { Signature } from '../../Signature';
import type { SignaturePanelProps } from '../weld/signatureTypes';
import {
  buildQcSignaturePayload,
  parseOptionalFloat,
  triStateToApi,
  type QcSignaturePayload,
  type SpoolStepPanelContext,
} from '../weld/spoolStepPayloadHelpers';
import {
  FieldGroup,
  FormGrid,
  StepInfoBanner,
  TriStateCheckRow,
  type TriStateCheck,
} from '../weld/spoolDetailFormUi';
import { getWeldStepPanelPayload, weldPanelsRevision } from '../weld/weldStepPanelHydration';
import { Card, CardContent } from '../../../../../../../components/ui/card';

dayjs.extend(customParseFormat);

export type FitUpSubmissionMode = 'draft' | 'accept';

/** Payload for fit-up draft / accept API (all form fields + QC signature context). */
export interface FitUpSavePayload {
  mode: FitUpSubmissionMode;
  projectId?: number | string | null;
  pipeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId: string | null;
  weldNumber: string | null;
  inspector: string;
  checkDate: string;
  bevelAngle: number | null;
  rootGap: number | null;
  hiLo: number | null;
  bevelMethod: string;
  jointCleanliness: 'pass' | 'fail' | 'pending';
  notes: string;
  signature: QcSignaturePayload;
}

export type FitUpPanelContext = SpoolStepPanelContext;

function normalizeCheckDateFromFit(raw?: string): string {
  if (!raw?.trim()) return dayjs().format('YYYY-MM-DD');
  const asIso = dayjs(raw);
  if (asIso.isValid()) return asIso.format('YYYY-MM-DD');
  const d2 = dayjs(raw, 'D MMM YYYY', true);
  if (d2.isValid()) return d2.format('YYYY-MM-DD');
  return dayjs().format('YYYY-MM-DD');
}

/** Normalize stored API / legacy values to display labels (saved value is always the label text). */
function normalizeBevelMethodDisplay(raw?: string): string {
  if (raw == null || String(raw).trim() === '') return 'Cold Cut';
  const s = String(raw).trim();
  const lower = s.toLowerCase().replace(/\s+/g, '_');
  if (lower === 'cold_cut' || lower === 'coldcut') return 'Cold Cut';
  if (lower === 'other') return 'Other';
  return s;
}

const DEFAULT_BEVEL_METHOD_LABELS = ['Cold Cut', 'Other'] as const;

/** Spools fitment spec limits from pipe FITMENT_READINGS (AddNewSpecification spools branch). */
export interface SpoolFitmentSpecLimits {
  specLabel?: string;
  internalHiLowMin?: number;
  internalHiLowMax?: number;
  wallThicknessTopRangeMin?: number;
  wallThicknessTopRangeMax?: number;
  gapToleranceMin?: number;
  gapToleranceMax?: number;
}

function formatSpecLimitValue(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(value);
}

/** Same source as production Fitment tab: latest file row on FITMENT_READINGS spec. */
export function parseSpoolFitmentSpecLimits(
  specifications: SpecificationDocument[] | undefined
): SpoolFitmentSpecLimits | null {
  if (!specifications?.length) return null;
  const fitmentSpec = specifications.find((s) => s.type === 'FITMENT_READINGS');
  if (!fitmentSpec) return null;

  const files = fitmentSpec.files;
  const fileRow =
    Array.isArray(files) && files.length > 0
      ? files[files.length - 1] ?? files[0]
      : null;
  const raw =
    (fileRow?.details?.fitmentReadings as Record<string, unknown> | undefined) ??
    (fitmentSpec.details?.fitmentReadings as Record<string, unknown> | undefined);
  if (!raw || typeof raw !== 'object') return null;

  const converted = convertFitmentReadingsObject(raw) as Record<string, unknown>;
  const num = (key: string) => {
    const v = converted[key];
    if (v == null || v === '') return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  return {
    specLabel: fileRow?.fileName ?? fitmentSpec.fileName ?? 'Fitment readings',
    internalHiLowMin: num('internalHiLowMin'),
    internalHiLowMax: num('internalHiLowMax'),
    wallThicknessTopRangeMin: num('wallThicknessTopRangeMin'),
    wallThicknessTopRangeMax: num('wallThicknessTopRangeMax'),
    gapToleranceMin: num('gapToleranceMin'),
    gapToleranceMax: num('gapToleranceMax'),
  };
}

function ReadOnlyMinMaxRow({
  title,
  unitLabel,
  min,
  max,
}: {
  title: string;
  unitLabel: string;
  min?: number;
  max?: number;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-gray-700">
        {title} ({unitLabel})
      </Label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-gray-500">Minimum</Label>
          <Input
            className="mt-1 h-9 text-sm bg-gray-50"
            readOnly
            tabIndex={-1}
            value={formatSpecLimitValue(min)}
          />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Maximum</Label>
          <Input
            className="mt-1 h-9 text-sm bg-gray-50"
            readOnly
            tabIndex={-1}
            value={formatSpecLimitValue(max)}
          />
        </div>
      </div>
    </div>
  );
}

export interface FitUpPanelProps {
  weld: WeldJoint | null;
  sig: SignaturePanelProps;
  context?: FitUpPanelContext;
  /** Pipe specifications (includes FITMENT_READINGS), same list as production Fitment tab. */
  specifications?: SpecificationDocument[];
  onFitUpSave?: (payload: FitUpSavePayload) => void | Promise<void>;
  /** Open the same Raise NCR dialog with the selected list row (fit-up NCRs only in table). */
  onSubmittedNcrClick?: (row: NcrRow) => void;
  /** Open Raise NCR for this step (no save). */
  onOpenRaiseNcr?: () => void;
  /** Set when opened from Accept / step repair workflow strip. */
  repairNo?: number | null;
}

export function FitUpPanel({
  weld,
  sig,
  context,
  specifications = [],
  onFitUpSave,
  onSubmittedNcrClick,
  onOpenRaiseNcr,
  repairNo = null,
}: FitUpPanelProps) {
  const dispatch = useDispatch();
  const ncrListAll = useSelector((s: { spoolsbase?: { ncrList?: NcrRow[] } }) => s.spoolsbase?.ncrList ?? []);
  const ncrList = useMemo(
    () => filterNcrRowsByWeldId(ncrListAll, weld?.id, { matchStepType: 'fitup' }),
    [ncrListAll, weld?.id]
  );
  

  
  const ncrListLoading = useSelector(
    (s: { spoolsbase?: { ncrListLoading?: boolean } }) => s.spoolsbase?.ncrListLoading ?? false
  );
  const [inspector, setInspector] = useState('J. Garcia (auto-filled)');
  const [checkDate, setCheckDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [bevelAngleStr, setBevelAngleStr] = useState('');
  const [rootGapStr, setRootGapStr] = useState('');
  const [hiLoStr, setHiLoStr] = useState('');
  const [wallThicknessStr, setWallThicknessStr] = useState('');
  const [gapStr, setGapStr] = useState('');
  const [bevelMethod, setBevelMethod] = useState('Cold Cut');
  /** Project-scoped rows from `/tags/getAllTags?tags_type=WELD_FITUP_BEVEL_METHOD_TAGS`. */
  const [bevelMethodTagsFromApi, setBevelMethodTagsFromApi] = useState<DocumentTag[]>([]);
  const [isAddingBevelMethod, setIsAddingBevelMethod] = useState(false);
  const [notes, setNotes] = useState('');
  const [cleanliness, setCleanliness] = useState<TriStateCheck>('neutral');
  const [draftSaved, setDraftSaved] = useState(false);
  const [activeSubmit, setActiveSubmit] = useState<'draft' | 'accept' | null>(null);
  /** Same tick as dispatch: Redux can flip to saving before `setState` is committed. */
  const activeSubmitRef = useRef<'draft' | 'accept' | null>(null);

  /** Sagas are async; parent only dispatches — track loading from Redux, not a void `await`. */
  const fitUpRemoteSaving = useSelector(
    (s: { spoolsbase?: { isSaving?: boolean; savingStep?: string | null } }) =>
      Boolean(s.spoolsbase?.isSaving && s.spoolsbase?.savingStep === 'fitUp')
  );

  const bevelMethodSelectOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const name of DEFAULT_BEVEL_METHOD_LABELS) {
      map.set(name, { id: name, name });
    }
    const pid = context?.projectId;
    for (const row of bevelMethodTagsFromApi) {
      const name = row.tags_name?.trim();
      if (!name) continue;
      if (pid != null && pid !== '' && row.project_id != null && String(row.project_id) !== String(pid)) {
        continue;
      }
      if (!map.has(name)) map.set(name, { id: name, name });
    }
    if (bevelMethod && !map.has(bevelMethod)) {
      map.set(bevelMethod, { id: bevelMethod, name: bevelMethod });
    }
    return Array.from(map.values());
  }, [bevelMethodTagsFromApi, bevelMethod, context?.projectId]);

  useEffect(() => {
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      setBevelMethodTagsFromApi([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const tags = await getListMaterialCategory(WELD_FITUP_BEVEL_METHOD_TAGS);
      if (cancelled || !Array.isArray(tags)) return;
      const forProject = tags.filter((t: DocumentTag) => String(t.project_id) === String(pid));
      setBevelMethodTagsFromApi(forProject);
    })();
    return () => {
      cancelled = true;
    };
  }, [context?.projectId]);

  const handleAddBevelMethod = async (name: string) => {
    const v = name.trim();
    if (!v) return;
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      Notification.warning('Project required', 'Select a project context before adding a bevel method.');
      setIsAddingBevelMethod(false);
      return;
    }
    if (bevelMethodSelectOptions.some((o) => o.name.toLowerCase() === v.toLowerCase())) {
      Notification.warning(`${v} already exists`, 'Choose another name or select it from the list.');
      setIsAddingBevelMethod(false);
      return;
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}') as { email?: string };
    const res = await AddMaterialCategory(
      {
        tags_name: v,
        project_id: pid,
        tags_type: WELD_FITUP_BEVEL_METHOD_TAGS,
        created_by: user.email || 'admin',
      },
      'Bevel method'
    );
    if (res) {
      setBevelMethodTagsFromApi((prev) => {
        const row = res as DocumentTag;
        if (prev.some((p) => p.tags_id === row.tags_id)) return prev;
        return [...prev, row];
      });
      setBevelMethod(v);
    }
    setIsAddingBevelMethod(false);
  };

  useEffect(() => {
    const pid = context?.projectId;
    const poid = context?.pipeId;
    if (pid == null || pid === '' || poid == null || poid === '') return;
    void dispatch(
      fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: context?.spoolId ?? undefined })
    );
  }, [context?.projectId, context?.pipeId, context?.spoolId, dispatch]);
  const lastSpoolStepSave = useSelector(
    (s: { spoolsbase?: { lastSaved?: { step: string; at: number; request?: { mode?: string; weldId?: string | null } } } }) =>
      s.spoolsbase?.lastSaved ?? null
  );

  useEffect(() => {
    if (!weld) {
      setInspector('J. Garcia (auto-filled)');
      setCheckDate(dayjs().format('YYYY-MM-DD'));
      setBevelAngleStr('');
      setRootGapStr('');
      setHiLoStr('');
      setWallThicknessStr('');
      setGapStr('');
      setBevelMethod('Cold Cut');
      setNotes('');
      setCleanliness('neutral');
      return;
    }
    const pl = getWeldStepPanelPayload(weld, 'fitUp');
    const fit = weld.fitUp;
    if (pl && Object.keys(pl).length > 0) {
      setInspector(
        pl.inspector != null ? String(pl.inspector) : fit?.inspector ?? 'J. Garcia (auto-filled)'
      );
      const cd = pl.checkDate != null ? String(pl.checkDate) : fit?.date;
      setCheckDate(normalizeCheckDateFromFit(cd));
      setBevelAngleStr(
        pl.bevelAngle != null ? String(pl.bevelAngle) : fit?.bevelAngle != null ? String(fit.bevelAngle) : ''
      );
      setRootGapStr(
        pl.rootGap != null ? String(pl.rootGap) : fit?.rootGap != null ? String(fit.rootGap) : ''
      );
      setHiLoStr(pl.hiLo != null ? String(pl.hiLo) : fit?.hiLo != null ? String(fit.hiLo) : '');
      {
        const raw =
          pl.bevelMethod != null ? String(pl.bevelMethod) : fit?.bevelMethod != null ? String(fit.bevelMethod) : '';
        const display = normalizeBevelMethodDisplay(raw);
        setBevelMethod(display);
      }
      const jc = pl.jointCleanliness;
      if (jc === 'pass' || jc === 'fail' || jc === 'pending') {
        setCleanliness(jc === 'pass' ? 'pass' : jc === 'fail' ? 'fail' : 'neutral');
      } else {
        const st = fit?.status;
        setCleanliness(
          st === 'pass' ? 'pass' : st === 'fail' ? 'fail' : 'neutral'
        );
      }
      setNotes(pl.notes != null ? String(pl.notes) : '');
      return;
    }
    if (fit) {
      setInspector(fit.inspector ?? 'J. Garcia (auto-filled)');
      setCheckDate(normalizeCheckDateFromFit(fit.date));
      setBevelAngleStr(fit.bevelAngle != null ? String(fit.bevelAngle) : '');
      setRootGapStr(fit.rootGap != null ? String(fit.rootGap) : '');
      setHiLoStr(fit.hiLo != null ? String(fit.hiLo) : '');
      {
        const display = normalizeBevelMethodDisplay(fit.bevelMethod != null ? String(fit.bevelMethod) : '');
        setBevelMethod(display);
      }
      const st = fit.status;
      setCleanliness(st === 'pass' ? 'pass' : st === 'fail' ? 'fail' : 'neutral');
      setNotes('');
    } else {
      setNotes('');
    }
  }, [weld, weld?.id, weldPanelsRevision(weld)]);

  const cycleCleanliness = () => {
    setCleanliness((cur) =>
      cur === 'neutral' ? 'pass' : cur === 'pass' ? 'fail' : 'neutral'
    );
  };

  const buildPayload = useCallback(
    (mode: FitUpSubmissionMode): FitUpSavePayload => ({
      mode,
      projectId: context?.projectId ?? null,
      pipeId: context?.pipeId ?? null,
      spoolId: context?.spoolId ?? null,
      spoolName: context?.spoolName ?? null,
      weldId: weld?.id ?? null,
      weldNumber: weld?.number ?? null,
      inspector,
      checkDate,
      bevelAngle: parseOptionalFloat(bevelAngleStr),
      rootGap: parseOptionalFloat(rootGapStr),
      hiLo: parseOptionalFloat(hiLoStr),
      bevelMethod,
      jointCleanliness: triStateToApi(cleanliness),
      notes: notes.trim(),
      signature: buildQcSignaturePayload(sig),
    }),
    [
      context?.projectId,
      context?.pipeId,
      context?.spoolId,
      context?.spoolName,
      weld?.id,
      weld?.number,
      inspector,
      checkDate,
      bevelAngleStr,
      rootGapStr,
      hiLoStr,
      bevelMethod,
      cleanliness,
      notes,
      sig,
    ]
  );

  const submit = (mode: FitUpSubmissionMode) => {
    if (!onFitUpSave) return;
    const payload = buildPayload(mode);
    activeSubmitRef.current = mode;
    setActiveSubmit(mode);
    onFitUpSave(payload);
  };

  useEffect(() => {
    if (!fitUpRemoteSaving) {
      activeSubmitRef.current = null;
      setActiveSubmit(null);
    }
  }, [fitUpRemoteSaving]);

  useEffect(() => {
    if (lastSpoolStepSave?.step !== 'fitUp') return;
    const req = lastSpoolStepSave.request;
    if (req?.weldId != null && weld?.id != null && req.weldId !== weld.id) return;
    if (req?.mode === 'draft') {
      setDraftSaved(true);
    }
  }, [lastSpoolStepSave, weld?.id]);

  const handleAcceptFitUp = () => {
    void submit('accept');
  };
  const handleSaveDraft = () => {
    void submit('draft');
  };

  const hasExistingFitUp = useMemo(() => {
    if (!weld) return false;
    if (weld.fitUp) return true;
    const pl = getWeldStepPanelPayload(weld, 'fitUp');
    return !!pl && Object.keys(pl).length > 0;
  }, [weld, weld?.id, weldPanelsRevision(weld)]);

  useEffect(() => {
    setDraftSaved(hasExistingFitUp);
  }, [hasExistingFitUp, weld?.id]);

  const hasQcSignature = Boolean(sig.selectedQcId) || Boolean(sig.qcSignatureData);
  const acceptReady = Boolean(weld) && draftSaved && hasQcSignature;
  const kind =
    activeSubmit ?? activeSubmitRef.current;
  const savingDraft = fitUpRemoteSaving && kind === 'draft';
  const savingAccept = fitUpRemoteSaving && kind === 'accept';
  const acceptDisabled = !acceptReady || savingDraft;

  const unitLabel = getUnitLabel(getUnitFromSettings());
  const fitmentSpecLimits = useMemo(
    () => parseSpoolFitmentSpecLimits(specifications),
    [specifications]
  );

  return (
    <div className="grid min-h-0 w-full grid-cols-1 gap-4">
      <Card className="flex h-[min(75vh,720px)] min-h-0 flex-col overflow-hidden border border-gray-200 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4">
      {repairNo != null && !Number.isNaN(Number(repairNo)) ? (
        <StepInfoBanner title="Repair fit-up" variant="notice">
          Repair cycle #{Number(repairNo)} — complete fit-up again before continuing the weld sequence.
        </StepInfoBanner>
      ) : null}
      {/* {ncrList.length > 0 && onSubmittedNcrClick && context?.projectId != null && context?.projectId !== '' && context?.pipeId != null && context?.pipeId !== '' ? (
        <section className="w-full shrink-0" aria-label="NCRs for this weld, Fit-Up">
          <WorkflowNcrSummaryTable
            ncrList={ncrList}
            loading={ncrListLoading}
            step="fitup"
            stepTitle="Fit-Up"
            weldId={weld?.id ?? null}
            onRowClick={onSubmittedNcrClick}
          />
        </section>
      ) : null} */}


      {fitmentSpecLimits ? (
        <section
          className="mt-2 space-y-3 rounded-lg border border-gray-200 bg-gray-50/80 p-3"
          aria-label="Fitment specification limits"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              Fitment specification limits
            </p>
            {fitmentSpecLimits.specLabel ? (
              <p className="mt-0.5 text-[11px] text-gray-500">{fitmentSpecLimits.specLabel}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ReadOnlyMinMaxRow
              title="Internal Hi-Low"
              unitLabel={unitLabel}
              min={fitmentSpecLimits.internalHiLowMin}
              max={fitmentSpecLimits.internalHiLowMax}
            />
            <ReadOnlyMinMaxRow
              title="Wall Thickness Top Range"
              unitLabel={unitLabel}
              min={fitmentSpecLimits.wallThicknessTopRangeMin}
              max={fitmentSpecLimits.wallThicknessTopRangeMax}
            />
            <ReadOnlyMinMaxRow
              title="Gap Tolerance Range"
              unitLabel={unitLabel}
              min={fitmentSpecLimits.gapToleranceMin}
              max={fitmentSpecLimits.gapToleranceMax}
            />
          </div>
        </section>
      ) : (
        <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          No FITMENT_READINGS specification found for this pipe. Add fitment readings under Project
          Specifications.
        </p>
      )}
      <div className="w-full min-w-0 space-y-4">
        <FormGrid>
        {/* <FieldGroup label="Inspector">
          <Input className="h-9 text-sm bg-gray-50" value={inspector} readOnly />
        </FieldGroup> */}
        <FieldGroup label="Check Date" required>
          <AntdDate className="h-9 text-sm" value={checkDate} onChange={setCheckDate} />
        </FieldGroup>
        <FieldGroup label="Bevel Angle (°)">
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 30.00"
            value={bevelAngleStr}
            onChange={(e) => setBevelAngleStr(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label="Root Gap (mm)">
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 3.00"
            value={rootGapStr}
            onChange={(e) => setRootGapStr(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label={`Internal Hi-Lo (${unitLabel})`}>
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 1.50"
            value={hiLoStr}
            onChange={(e) => setHiLoStr(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label={`Wall Thickness (${unitLabel})`}>
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 1.50"
            value={wallThicknessStr}
            onChange={(e) => setWallThicknessStr(e.target.value)}
          />
        </FieldGroup>
        <FieldGroup label={`Gap (${unitLabel})`}>
          <Input
            className="h-9 text-sm"
            placeholder="e.g. 1.50"
            value={gapStr}
            onChange={(e) => setGapStr(e.target.value)}
          />
        </FieldGroup>
        

        <FieldGroup label="Bevel Method">
          <SearchableSelect
            value={bevelMethod}
            onValueChange={setBevelMethod}
            placeholder="Select bevel method"
            searchPlaceholder="Search bevel methods…"
            options={bevelMethodSelectOptions}
            onAddNew={() => setIsAddingBevelMethod(true)}
            addNewText="Add bevel method"
            className="w-full [&_button]:h-9 [&_button]:text-sm"
          />
        </FieldGroup>
        {isAddingBevelMethod && (
          <AddNewItemDialog
            open={isAddingBevelMethod}
            onOpenChange={setIsAddingBevelMethod}
            title="Add bevel method"
            label="Bevel method"
            placeholder="e.g. Plasma cut"
            onAdd={handleAddBevelMethod}
            buttonText="Add"
            cancelText="Cancel"
          />
        )}
        </FormGrid>
        <div className="space-y-2 mt-2">
        <TriStateCheckRow
          tag="Fit-up"
          labels={{
            neutral: 'Joint cleanliness — pending',
            pass: 'Joint cleanliness acceptable',
            fail: 'Joint cleanliness — not acceptable',
          }}
          state={cleanliness}
          onCycle={cycleCleanliness}
        />
        </div>
        <div className="space-y-2 mt-2">
        <Label>Notes</Label>
        <textarea
          className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-[#e04530] focus:border-[#e04530]"
          placeholder="Observations..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        </div>

        <Signature
        customerSignature={null}
        qcSignature={sig.selectedQcId}
        customerRepresentativeList={[]}
        qcList={sig.qcList}
        handleQcSignatureChange={sig.onQcChange}
        signatureData={sig.qcSignatureData}
        onSignatureChange={sig.onQcSignatureChange}
        showCustomerRep={false}
        showQc={true}
        qcLabel="QC Inspector"
        canSign={true}
        canEdit={true}
        isEditMode={true}
        isAdminOrSuperAdmin={true}
        />

        <div className="mt-4 flex justify-end gap-2">
        {/* <Button
          variant="outline"
          size="sm"
          type="button"
          className="border-red-300 text-red-600 hover:bg-red-50"
          disabled={!weld || savingDraft || savingAccept}
          onClick={() => onOpenRaiseNcr?.()}
        >
          Fail — Raise NCR
        </Button> */}
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={!weld || savingAccept}
          loading={savingDraft}
          loadingText="Saving draft…"
          onClick={handleSaveDraft}
        >
          Save Draft
        </Button>
        <Button
          type="button"
          size="sm"
          className="bg-[#e04530] hover:bg-[#e04530]/90 text-white"
          disabled={acceptDisabled || !weld || !hasQcSignature}
          loading={savingAccept}
          loadingText={hasExistingFitUp ? 'Updating…' : 'Accepting…'}
          onClick={handleAcceptFitUp}
        >
          Accept Fit-Up
        </Button>
        </div>
      </div>
        </CardContent>
      </Card>
    </div>
  );
}
